import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Typography, TextField, Button, Paper } from "@material-ui/core";
import { updateEventAttribute } from "../../../../../../data-store/actions/events-actions";
import {
  TEXTO_AVISO_PRIVACIDAD_DEFAULT,
  TEXTO_LEGAL_MAYORES_DEFAULT,
  TEXTO_LEGAL_MENORES_DEFAULT,
} from "./deslindeTextos";
import { descargarPdfDeslindeGenerico } from "./deslindeGenericoPdf";

// Una sección de texto editable (aviso de privacidad, o el párrafo legal por
// edad): textarea con el texto guardado (o el default si el evento no tiene
// override), Guardar, Restaurar y —cuando aplica— descargar el PDF genérico
// usando el texto actual del textarea (aunque todavía no se haya guardado,
// para poder revisarlo antes de confirmar). onBorradorChange reporta cada
// cambio al padre, para que las secciones de mayores/menores puedan armar el
// PDF con el aviso más reciente aunque viva en otra sección.
function SeccionDeslinde({
  titulo,
  placeholders,
  valorGuardado,
  textoDefault,
  rows = 12,
  onGuardar,
  onRestaurar,
  onDescargar,
  onBorradorChange,
}) {
  const [borrador, setBorrador] = useState(valorGuardado || textoDefault);

  useEffect(() => {
    setBorrador(valorGuardado || textoDefault);
  }, [valorGuardado, textoDefault]);

  useEffect(() => {
    if (onBorradorChange) onBorradorChange(borrador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrador]);

  const tieneOverride = !!valorGuardado;

  return (
    <Paper style={{ padding: 20, marginBottom: 20 }}>
      <Typography variant="h5" gutterBottom>
        {titulo}
      </Typography>
      <Typography variant="body2" style={{ opacity: 0.75, marginBottom: 10 }}>
        Puedes usar{" "}
        {placeholders.map((p, i) => (
          <React.Fragment key={p}>
            {i > 0 && ", "}
            <code>{`{${p}}`}</code>
          </React.Fragment>
        ))}{" "}
        dentro del texto: se reemplaza automáticamente.{" "}
        {tieneOverride
          ? "Este evento ya tiene un texto personalizado guardado."
          : "Actualmente se usa el texto por defecto (no hay personalización guardada)."}
      </Typography>
      <TextField
        multiline
        fullWidth
        rows={rows}
        variant="outlined"
        value={borrador}
        onChange={(e) => setBorrador(e.target.value)}
      />
      <Box mt={2} display="flex" gap={12} flexWrap="wrap">
        <Button variant="contained" color="primary" onClick={() => onGuardar(borrador)}>
          Guardar
        </Button>
        <Button variant="outlined" onClick={onRestaurar} disabled={!tieneOverride}>
          Restaurar texto original
        </Button>
        {onDescargar && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => onDescargar(borrador)}
          >
            Descargar deslinde genérico
          </Button>
        )}
      </Box>
    </Paper>
  );
}

export default function DeslindeConfigContainer() {
  const dispatch = useDispatch();
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const registryConfiguration = useSelector(
    (state) => state.registry.registryConfiguration
  );

  const eventoIndex = "evento_" + eventDetail.iDEvento;
  const deslindeConfig =
    (registryConfiguration && registryConfiguration.deslindeResponsabilidades) || {};
  const nombreEvento = eventDetail.nombreEvento;
  const linkAvisoPrivacidad =
    eventDetail.organizador && eventDetail.organizador.linkAvisoPrivacidad;

  // El párrafo de aviso vive en su propia sección, pero los botones de
  // descarga de mayores/menores lo necesitan para armar el documento
  // completo — se guarda el borrador más reciente aquí.
  const [avisoBorrador, setAvisoBorrador] = useState(
    deslindeConfig.textoAviso || TEXTO_AVISO_PRIVACIDAD_DEFAULT
  );

  function guardar(campo, valor) {
    dispatch(
      updateEventAttribute(
        eventoIndex,
        "inscripcionesConfiguracion/deslindeResponsabilidades/",
        { [campo]: valor }
      )
    );
  }

  function restaurar(campo) {
    dispatch(
      updateEventAttribute(
        eventoIndex,
        "inscripcionesConfiguracion/deslindeResponsabilidades/",
        { [campo]: null }
      )
    );
  }

  return (
    <Box>
      <SeccionDeslinde
        titulo="Aviso de privacidad y entrega de kit"
        placeholders={["linkAvisoPrivacidad"]}
        valorGuardado={deslindeConfig.textoAviso}
        textoDefault={TEXTO_AVISO_PRIVACIDAD_DEFAULT}
        rows={4}
        onGuardar={(valor) => guardar("textoAviso", valor)}
        onRestaurar={() => restaurar("textoAviso")}
        onBorradorChange={setAvisoBorrador}
      />
      <SeccionDeslinde
        titulo="Mayores de edad"
        placeholders={["nombreEvento"]}
        valorGuardado={deslindeConfig.textoMayores}
        textoDefault={TEXTO_LEGAL_MAYORES_DEFAULT}
        onGuardar={(valor) => guardar("textoMayores", valor)}
        onRestaurar={() => restaurar("textoMayores")}
        onDescargar={(valor) =>
          descargarPdfDeslindeGenerico({
            nombreEvento,
            linkAvisoPrivacidad,
            textoAviso: avisoBorrador,
            textoLegal: valor,
            esMenor: false,
          })
        }
      />
      <SeccionDeslinde
        titulo="Menores de edad"
        placeholders={["nombreEvento"]}
        valorGuardado={deslindeConfig.textoMenores}
        textoDefault={TEXTO_LEGAL_MENORES_DEFAULT}
        onGuardar={(valor) => guardar("textoMenores", valor)}
        onRestaurar={() => restaurar("textoMenores")}
        onDescargar={(valor) =>
          descargarPdfDeslindeGenerico({
            nombreEvento,
            linkAvisoPrivacidad,
            textoAviso: avisoBorrador,
            textoLegal: valor,
            esMenor: true,
          })
        }
      />
    </Box>
  );
}
